import React, { useState, useEffect } from 'react';
import { ExternalLink, X } from 'lucide-react';
import OddsButton from './OddsButton';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';
import TeamLogo from './TeamLogo';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Takes last word of team name, but uses last 2 words for ambiguous suffixes like "Sox"
const AMBIGUOUS_SUFFIXES = new Set(['sox']);
function shortTeamName(name) {
  if (!name) return '';
  const parts = name.split(' ');
  if (parts.length >= 2 && AMBIGUOUS_SUFFIXES.has(parts[parts.length - 1].toLowerCase())) {
    return parts.slice(-2).join(' ');
  }
  return parts[parts.length - 1];
}

function formatPeriod(period, sportKey) {
  if (!period) return '';
  if (sportKey?.includes('basketball')) return `Q${period}`;
  if (sportKey?.includes('hockey')) return `P${period}`;
  if (sportKey?.includes('baseball')) {
    const sfx = ['st','nd','rd'][period-1] || 'th';
    return `${period}${sfx} Inn`;
  }
  if (sportKey?.includes('football')) return `Q${period}`;
  if (sportKey?.includes('tennis')) return `Set ${period}`;
  return `P${period}`;
}

const SPORTSBOOK_URLS = {
  draftkings: 'https://sportsbook.draftkings.com',
  fanduel: 'https://sportsbook.fanduel.com',
  betmgm: 'https://sports.betmgm.com',
  williamhill_us: 'https://www.caesars.com/sportsbook-and-casino',
  fanatics: 'https://sportsbook.fanatics.com',
  espnbet: 'https://sportsbook.thescore.bet',
  ballybet: 'https://www.ballybet.com',
  betrivers: 'https://www.betrivers.com',
};

function GameCard({ event, prevOdds = {}, isHighlighted = false, highlightRef = null, onClearHighlight, onSelectGame, evOpps = [], arbOpps = [], altFetchDelay = 0, maxOdds = null, selectedBooks = [] }) {
  const { session } = useAuth();
  const { addSelection, removeSelection, selections } = useCart();

  const [altLines, setAltLines] = useState(null);
  const [trackedKeys, setTrackedKeys] = useState(new Set());
  // activeCell: null | { key, stage: 'choice' | 'track' | 'edit' }
  const [activeCell, setActiveCell] = useState(null);
  const [trackAmount, setTrackAmount] = useState('');
  const [editedOddsMap, setEditedOddsMap] = useState({});
  const [altLoading, setAltLoading] = useState(false);
  const [altSpreadsLimit, setAltSpreadsLimit] = useState(3);
  const [altTotalsLimit, setAltTotalsLimit] = useState(3);
  const isExpanded = false;

  useEffect(() => {
    setAltLoading(true);
    const timer = setTimeout(() => {
      fetch(`${API_BASE_URL}/events/${event.id}/alternate-odds?sport=${event.sport}`)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(data => { setAltLines(data); setAltLoading(false); })
        .catch(err => {
          console.error(`Alt lines failed for ${event.home_team} vs ${event.away_team}:`, err.message);
          setAltLoading(false);
        });
    }, altFetchDelay);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- h2h best odds ---
  const bestHomeOdds = Math.max(...event.bookmakers.map(b => b.home_odds));
  const bestAwayOdds = Math.max(...event.bookmakers.map(b => b.away_odds));
  const hasDrawOdds = event.has_draw || event.bookmakers.some(b => b.draw_odds);
  const booksWithDraw = event.bookmakers.filter(b => b.draw_odds);
  const bestDrawOdds = hasDrawOdds && booksWithDraw.length > 0
    ? Math.max(...booksWithDraw.map(b => b.draw_odds))
    : null;

  const homeOddsByBook = {};
  const awayOddsByBook = {};
  const drawOddsByBook = {};
  event.bookmakers.forEach(b => {
    homeOddsByBook[b.name] = b.home_odds;
    awayOddsByBook[b.name] = b.away_odds;
    if (b.draw_odds) drawOddsByBook[b.name] = b.draw_odds;
  });

  const evSet = new Set(
    evOpps.map(o => `${o.sportsbook_key}:${o.team}:${o.market}:${o.point ?? ''}`)
  );
  // Only show ARB badge when user has both sides' books selected (empty selectedBooks = all books)
  const visibleArbOpps = selectedBooks.length === 0
    ? arbOpps
    : arbOpps.filter(o =>
        selectedBooks.includes(o.home_sportsbook_key) &&
        selectedBooks.includes(o.away_sportsbook_key)
      );
  const arbHome = {};
  const arbAway = {};
  visibleArbOpps.forEach(o => {
    const k = `${o.market ?? 'h2h'}:${o.point ?? o.home_point ?? ''}`;
    arbHome[k] = o.home_sportsbook_key;
    arbAway[k] = o.away_sportsbook_key;
  });

  const americanToProb = (odds) =>
    odds > 0 ? 100 / (odds + 100) : Math.abs(odds) / (Math.abs(odds) + 100);

  const altArbHome = {};
  const altArbAway = {};
  if (altLines) {
    Object.entries(altLines.spread_lines || {}).forEach(([pointStr, books]) => {
      if (books.length < 2) return;
      const numPoint = Number(pointStr);
      const bestH = books.reduce((b, c) => c.home_spread_odds > b.home_spread_odds ? c : b, books[0]);
      const bestA = books.reduce((b, c) => c.away_spread_odds > b.away_spread_odds ? c : b, books[0]);
      const booksOk = selectedBooks.length === 0 ||
        (selectedBooks.includes(bestH.name) && selectedBooks.includes(bestA.name));
      if (bestH.name !== bestA.name && booksOk &&
          americanToProb(bestH.home_spread_odds) + americanToProb(bestA.away_spread_odds) < 1.0) {
        altArbHome[`spreads:${numPoint}`] = bestH.name;
        altArbAway[`spreads:${numPoint}`] = bestA.name;
      }
    });
    Object.entries(altLines.total_lines || {}).forEach(([pointStr, books]) => {
      if (books.length < 2) return;
      const numPoint = Number(pointStr);
      const bestO = books.reduce((b, c) => c.over_odds  > b.over_odds  ? c : b, books[0]);
      const bestU = books.reduce((b, c) => c.under_odds > b.under_odds ? c : b, books[0]);
      const booksOk = selectedBooks.length === 0 ||
        (selectedBooks.includes(bestO.name) && selectedBooks.includes(bestU.name));
      if (bestO.name !== bestU.name && booksOk &&
          americanToProb(bestO.over_odds) + americanToProb(bestU.under_odds) < 1.0) {
        altArbHome[`totals:${numPoint}`] = bestO.name;
        altArbAway[`totals:${numPoint}`] = bestU.name;
      }
    });
  }

  const ALT_EV_THRESHOLD = 0.02;
  const altEvSpreadsHome = new Set();
  const altEvSpreadsAway = new Set();
  const altEvTotalsOver  = new Set();
  const altEvTotalsUnder = new Set();

  if (altLines) {
    Object.entries(altLines.spread_lines || {}).forEach(([pointStr, books]) => {
      if (books.length < 2) return;
      const numPoint = Number(pointStr);
      const noVig = books.map(b => {
        const ph = americanToProb(b.home_spread_odds);
        const pa = americanToProb(b.away_spread_odds);
        return { name: b.name, nvh: ph / (ph + pa), nva: pa / (ph + pa) };
      });
      const avgH = noVig.reduce((s, b) => s + b.nvh, 0) / noVig.length;
      const avgA = 1 - avgH;
      books.forEach(b => {
        if (avgH - americanToProb(b.home_spread_odds) > ALT_EV_THRESHOLD &&
            (maxOdds === null || b.home_spread_odds <= maxOdds))
          altEvSpreadsHome.add(`${b.name}:${numPoint}`);
        if (avgA - americanToProb(b.away_spread_odds) > ALT_EV_THRESHOLD &&
            (maxOdds === null || b.away_spread_odds <= maxOdds))
          altEvSpreadsAway.add(`${b.name}:${numPoint}`);
      });
    });
    Object.entries(altLines.total_lines || {}).forEach(([pointStr, books]) => {
      if (books.length < 2) return;
      const numPoint = Number(pointStr);
      const noVig = books.map(b => {
        const po = americanToProb(b.over_odds);
        const pu = americanToProb(b.under_odds);
        return { name: b.name, nvo: po / (po + pu), nvu: pu / (po + pu) };
      });
      const avgO = noVig.reduce((s, b) => s + b.nvo, 0) / noVig.length;
      const avgU = 1 - avgO;
      books.forEach(b => {
        if (avgO - americanToProb(b.over_odds) > ALT_EV_THRESHOLD &&
            (maxOdds === null || b.over_odds <= maxOdds))
          altEvTotalsOver.add(`${b.name}:${numPoint}`);
        if (avgU - americanToProb(b.under_odds) > ALT_EV_THRESHOLD &&
            (maxOdds === null || b.under_odds <= maxOdds))
          altEvTotalsUnder.add(`${b.name}:${numPoint}`);
      });
    });
  }


  const formatPoint = (point) => {
    if (point == null) return '';
    return point > 0 ? `+${point}` : `${point}`;
  };

  const eventName = `${event.home_team} vs ${event.away_team}`;

  const getBooksForPoint = (linesDict, numericPoint) => {
    const entry = Object.entries(linesDict || {}).find(([k]) => Number(k) === numericPoint);
    return entry ? entry[1] : null;
  };

  async function handleTrackBet(key, team, odds, bookKey, market, point) {
    if (!session) return;
    const side = market === 'spreads'
      ? `${team} ${point > 0 ? '+' : ''}${point}`
      : market === 'totals'
      ? `${team} ${point}`
      : team;
    const notesPoint = market === 'spreads'
      ? (team === event.home_team ? point : -point)
      : market === 'totals' ? point : null;
    try {
      await fetch(`${API_BASE_URL}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'manual',
          sportsbook: bookKey,
          eventName,
          sportKey: event.sport,
          market,
          side,
          odds,
          amount: parseFloat(trackAmount) || 0,
          notes: JSON.stringify({
            event_id: event.id,
            sport_key: event.sport,
            home_team: event.home_team,
            away_team: event.away_team,
            side: team,
            point: notesPoint,
            commence_time: event.commence_time,
          }),
        }),
      });
      setTrackedKeys(prev => { const next = new Set(prev); next.add(key); return next; });
      setActiveCell(null);
      setTrackAmount('');
    } catch (err) {
      console.error('Track failed:', err);
    }
  }

  // ── Full expanded view renderers ──────────────────────────────────────────────
  const renderH2hOdds = () => (
    <div className="odds-section">
      <div className={`odds-row header ${hasDrawOdds ? 'three-way' : ''}`}>
        <span className="book-col">Sportsbook</span>
        <span className="odds-col">{shortTeamName(event.home_team)}</span>
        {hasDrawOdds && <span className="odds-col">Draw</span>}
        <span className="odds-col">{shortTeamName(event.away_team)}</span>
      </div>
      {event.bookmakers.map((book, idx) => {
        const oddsKey = `${event.id}_${book.name}`;
        const homeMovement = prevOdds[`${oddsKey}_home`] != null
          ? book.home_odds - prevOdds[`${oddsKey}_home`] : 0;
        const awayMovement = prevOdds[`${oddsKey}_away`] != null
          ? book.away_odds - prevOdds[`${oddsKey}_away`] : 0;
        const drawMovement = book.draw_odds && prevOdds[`${oddsKey}_draw`] != null
          ? book.draw_odds - prevOdds[`${oddsKey}_draw`] : 0;
        const arbKey = 'h2h:';
        return (
          <div key={idx} className={`odds-row ${hasDrawOdds ? 'three-way' : ''}`}>
            <span className="book-col">
              <SportsbookLogo bookKey={book.name} size={18} />
              {book.title || SPORTSBOOK_NAMES[book.name] || book.name}
              {(book.pm_url || SPORTSBOOK_URLS[book.name]) && (
                <a href={book.pm_url || SPORTSBOOK_URLS[book.name]} target="_blank" rel="noopener noreferrer"
                  className="quick-bet-link" title={`Open ${book.title}`}>
                  <ExternalLink size={12} />
                </a>
              )}
            </span>
            <OddsButton
              odds={book.home_odds}
              isBest={book.home_odds === bestHomeOdds}
              isEV={evSet.has(`${book.name}:${event.home_team}:h2h:`)}
              isArb={arbHome[arbKey] === book.name}
              movement={homeMovement}
              selectionData={{ eventId: event.id, eventName, team: event.home_team,
                odds: book.home_odds, sportsbook: book.title, sportsbookKey: book.name,
                oddsAllBooks: homeOddsByBook, market: 'h2h' }}
            />
            {hasDrawOdds && (
              <OddsButton
                odds={book.draw_odds}
                isBest={book.draw_odds === bestDrawOdds}
                isEV={evSet.has(`${book.name}:Draw:h2h:`)}
                isArb={false}
                movement={drawMovement}
                selectionData={{ eventId: event.id, eventName, team: 'Draw',
                  odds: book.draw_odds, sportsbook: book.title, sportsbookKey: book.name,
                  oddsAllBooks: drawOddsByBook, market: 'h2h' }}
              />
            )}
            <OddsButton
              odds={book.away_odds}
              isBest={book.away_odds === bestAwayOdds}
              isEV={evSet.has(`${book.name}:${event.away_team}:h2h:`)}
              isArb={arbAway[arbKey] === book.name}
              movement={awayMovement}
              selectionData={{ eventId: event.id, eventName, team: event.away_team,
                odds: book.away_odds, sportsbook: book.title, sportsbookKey: book.name,
                oddsAllBooks: awayOddsByBook, market: 'h2h' }}
            />
          </div>
        );
      })}
    </div>
  );

  const renderConsensusSpreadOdds = () => {
    const cs = event.consensus_spread;
    const books = getBooksForPoint(event.spread_lines, cs);
    if (!books || books.length === 0)
      return <p className="no-market-data">No spread data available</p>;
    const bestHome = Math.max(...books.map(b => b.home_spread_odds));
    const bestAway = Math.max(...books.map(b => b.away_spread_odds));
    const arbKey = `spreads:${cs}`;
    return (
      <div className="odds-section">
        <div className="odds-row header">
          <span className="book-col">Sportsbook</span>
          <span className="odds-col">{shortTeamName(event.home_team)} {formatPoint(cs)}</span>
          <span className="odds-col">{shortTeamName(event.away_team)} {formatPoint(-cs)}</span>
        </div>
        {books.map((book, idx) => {
          const bookUrl = SPORTSBOOK_URLS[book.name];
          return (
            <div key={idx} className="odds-row">
              <span className="book-col">
                <SportsbookLogo bookKey={book.name} size={18} />
                {book.title || SPORTSBOOK_NAMES[book.name] || book.name}
                {bookUrl && (
                  <a href={bookUrl} target="_blank" rel="noopener noreferrer"
                    className="quick-bet-link" title={`Open ${book.title}`}>
                    <ExternalLink size={12} />
                  </a>
                )}
              </span>
              <OddsButton
                odds={book.home_spread_odds}
                isBest={book.home_spread_odds === bestHome}
                isEV={evSet.has(`${book.name}:${event.home_team}:spreads:${cs}`)}
                isArb={arbHome[arbKey] === book.name}
                selectionData={{ eventId: event.id, eventName, team: event.home_team,
                  odds: book.home_spread_odds, sportsbook: book.title, sportsbookKey: book.name,
                  market: 'spreads', point: cs }}
              />
              <OddsButton
                odds={book.away_spread_odds}
                isBest={book.away_spread_odds === bestAway}
                isEV={evSet.has(`${book.name}:${event.away_team}:spreads:${cs}`)}
                isArb={arbAway[arbKey] === book.name}
                selectionData={{ eventId: event.id, eventName, team: event.away_team,
                  odds: book.away_spread_odds, sportsbook: book.title, sportsbookKey: book.name,
                  market: 'spreads', point: -cs }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderAltSpreadOdds = () => {
    const lines = altLines?.spread_lines || {};
    const allPoints = Object.keys(lines);
    if (allPoints.length === 0) return <p className="no-market-data">No alternate spread data available</p>;

    const cs = event.consensus_spread;
    const byDistance = [...allPoints].sort((a, b) => {
      const da = cs != null ? Math.abs(Number(a) - cs) : 0;
      const db = cs != null ? Math.abs(Number(b) - cs) : 0;
      return da !== db ? da - db : Number(a) - Number(b);
    });

    const totalCount = byDistance.length;
    const limit = Math.min(altSpreadsLimit, totalCount);
    const displayPoints = byDistance.slice(0, limit);
    const remaining = totalCount - limit;
    const nextChunk = Math.min(3, remaining);
    const isExp = altSpreadsLimit > 3;

    const hiddenHasOpp = remaining > 0 && byDistance.slice(limit).some(p => {
      const n = Number(p);
      return altArbHome[`spreads:${n}`] ||
        (lines[p] || []).some(b =>
          altEvSpreadsHome.has(`${b.name}:${n}`) || altEvSpreadsAway.has(`${b.name}:${n}`)
        );
    });

    const renderLineGroup = (point) => {
      const books = lines[point];
      const numPoint = Number(point);
      const bestHome = Math.max(...books.map(b => b.home_spread_odds));
      const bestAway = Math.max(...books.map(b => b.away_spread_odds));
      const arbKey = `spreads:${numPoint}`;
      return (
        <div key={point} className="alt-line-group">
          <div className="alt-line-header">
            {shortTeamName(event.home_team)} {formatPoint(numPoint)} /&nbsp;
            {shortTeamName(event.away_team)} {formatPoint(-numPoint)}
          </div>
          <div className="odds-row header">
            <span className="book-col">Sportsbook</span>
            <span className="odds-col">{shortTeamName(event.home_team)}</span>
            <span className="odds-col">{shortTeamName(event.away_team)}</span>
          </div>
          {books.map((book, idx) => {
            const bookUrl = SPORTSBOOK_URLS[book.name];
            return (
              <div key={idx} className="odds-row">
                <span className="book-col">
                  <SportsbookLogo bookKey={book.name} size={18} />
                  {book.title || SPORTSBOOK_NAMES[book.name] || book.name}
                  {bookUrl && (
                    <a href={bookUrl} target="_blank" rel="noopener noreferrer"
                      className="quick-bet-link" title={`Open ${book.title}`}>
                      <ExternalLink size={12} />
                    </a>
                  )}
                </span>
                <OddsButton
                  odds={book.home_spread_odds}
                  isBest={book.home_spread_odds === bestHome}
                  isEV={altEvSpreadsHome.has(`${book.name}:${numPoint}`)}
                  isArb={altArbHome[arbKey] === book.name}
                  selectionData={{ eventId: event.id, eventName, team: event.home_team,
                    odds: book.home_spread_odds, sportsbook: book.title, sportsbookKey: book.name,
                    market: 'spreads', point: numPoint }}
                />
                <OddsButton
                  odds={book.away_spread_odds}
                  isBest={book.away_spread_odds === bestAway}
                  isEV={altEvSpreadsAway.has(`${book.name}:${numPoint}`)}
                  isArb={altArbAway[arbKey] === book.name}
                  selectionData={{ eventId: event.id, eventName, team: event.away_team,
                    odds: book.away_spread_odds, sportsbook: book.title, sportsbookKey: book.name,
                    market: 'spreads', point: -numPoint }}
                />
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="odds-section">
        {displayPoints.map(renderLineGroup)}
        {(remaining > 0 || isExp) && (
          <div className="alt-pagination">
            {remaining > 0 && (
              <button
                className={`alt-expand-btn${hiddenHasOpp ? ' has-opp' : ''}`}
                onClick={(e) => { e.stopPropagation(); setAltSpreadsLimit(l => l + 3); }}
              >
                {hiddenHasOpp ? '⚡ ' : ''}+{nextChunk} more
              </button>
            )}
            {remaining > nextChunk && (
              <button
                className="alt-expand-btn"
                onClick={(e) => { e.stopPropagation(); setAltSpreadsLimit(Infinity); }}
              >
                See all {totalCount}
              </button>
            )}
            {isExp && (
              <button
                className="alt-expand-btn"
                onClick={(e) => { e.stopPropagation(); setAltSpreadsLimit(3); }}
              >
                Back to main
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderConsensusTotalsOdds = () => {
    const ct = event.consensus_total;
    const books = getBooksForPoint(event.total_lines, ct);
    if (!books || books.length === 0)
      return <p className="no-market-data">No totals data available</p>;
    const bestOver  = Math.max(...books.map(b => b.over_odds));
    const bestUnder = Math.max(...books.map(b => b.under_odds));
    const arbKey = `totals:${ct}`;
    return (
      <div className="odds-section">
        <div className="odds-row header">
          <span className="book-col">Sportsbook</span>
          <span className="odds-col">Over {ct}</span>
          <span className="odds-col">Under {ct}</span>
        </div>
        {books.map((book, idx) => {
          const bookUrl = SPORTSBOOK_URLS[book.name];
          return (
            <div key={idx} className="odds-row">
              <span className="book-col">
                <SportsbookLogo bookKey={book.name} size={18} />
                {book.title || SPORTSBOOK_NAMES[book.name] || book.name}
                {bookUrl && (
                  <a href={bookUrl} target="_blank" rel="noopener noreferrer"
                    className="quick-bet-link" title={`Open ${book.title}`}>
                    <ExternalLink size={12} />
                  </a>
                )}
              </span>
              <OddsButton
                odds={book.over_odds}
                isBest={book.over_odds === bestOver}
                isEV={evSet.has(`${book.name}:Over:totals:${ct}`)}
                isArb={arbHome[arbKey] === book.name}
                selectionData={{ eventId: event.id, eventName, team: 'Over',
                  odds: book.over_odds, sportsbook: book.title, sportsbookKey: book.name,
                  market: 'totals', point: ct }}
              />
              <OddsButton
                odds={book.under_odds}
                isBest={book.under_odds === bestUnder}
                isEV={evSet.has(`${book.name}:Under:totals:${ct}`)}
                isArb={arbAway[arbKey] === book.name}
                selectionData={{ eventId: event.id, eventName, team: 'Under',
                  odds: book.under_odds, sportsbook: book.title, sportsbookKey: book.name,
                  market: 'totals', point: ct }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderAltTotalsOdds = () => {
    const lines = altLines?.total_lines || {};
    const allPoints = Object.keys(lines);
    if (allPoints.length === 0) return <p className="no-market-data">No alternate totals data available</p>;

    const ct = event.consensus_total;
    const byDistance = [...allPoints].sort((a, b) => {
      const da = ct != null ? Math.abs(Number(a) - ct) : 0;
      const db = ct != null ? Math.abs(Number(b) - ct) : 0;
      return da !== db ? da - db : Number(a) - Number(b);
    });

    const totalCount = byDistance.length;
    const limit = Math.min(altTotalsLimit, totalCount);
    const displayPoints = byDistance.slice(0, limit);
    const remaining = totalCount - limit;
    const nextChunk = Math.min(3, remaining);
    const isExp = altTotalsLimit > 3;

    const hiddenHasOpp = remaining > 0 && byDistance.slice(limit).some(p => {
      const n = Number(p);
      return altArbHome[`totals:${n}`] ||
        (lines[p] || []).some(b =>
          altEvTotalsOver.has(`${b.name}:${n}`) || altEvTotalsUnder.has(`${b.name}:${n}`)
        );
    });

    const renderLineGroup = (point) => {
      const books = lines[point];
      const numPoint = Number(point);
      const bestOver  = Math.max(...books.map(b => b.over_odds));
      const bestUnder = Math.max(...books.map(b => b.under_odds));
      const arbKey = `totals:${numPoint}`;
      return (
        <div key={point} className="alt-line-group">
          <div className="alt-line-header">O/U {numPoint}</div>
          <div className="odds-row header">
            <span className="book-col">Sportsbook</span>
            <span className="odds-col">Over {numPoint}</span>
            <span className="odds-col">Under {numPoint}</span>
          </div>
          {books.map((book, idx) => {
            const bookUrl = SPORTSBOOK_URLS[book.name];
            return (
              <div key={idx} className="odds-row">
                <span className="book-col">
                  <SportsbookLogo bookKey={book.name} size={18} />
                  {book.title || SPORTSBOOK_NAMES[book.name] || book.name}
                  {bookUrl && (
                    <a href={bookUrl} target="_blank" rel="noopener noreferrer"
                      className="quick-bet-link" title={`Open ${book.title}`}>
                      <ExternalLink size={12} />
                    </a>
                  )}
                </span>
                <OddsButton
                  odds={book.over_odds}
                  isBest={book.over_odds === bestOver}
                  isEV={altEvTotalsOver.has(`${book.name}:${numPoint}`)}
                  isArb={altArbHome[arbKey] === book.name}
                  selectionData={{ eventId: event.id, eventName, team: 'Over',
                    odds: book.over_odds, sportsbook: book.title, sportsbookKey: book.name,
                    market: 'totals', point: numPoint }}
                />
                <OddsButton
                  odds={book.under_odds}
                  isBest={book.under_odds === bestUnder}
                  isEV={altEvTotalsUnder.has(`${book.name}:${numPoint}`)}
                  isArb={altArbAway[arbKey] === book.name}
                  selectionData={{ eventId: event.id, eventName, team: 'Under',
                    odds: book.under_odds, sportsbook: book.title, sportsbookKey: book.name,
                    market: 'totals', point: numPoint }}
                />
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="odds-section">
        {displayPoints.map(renderLineGroup)}
        {(remaining > 0 || isExp) && (
          <div className="alt-pagination">
            {remaining > 0 && (
              <button
                className={`alt-expand-btn${hiddenHasOpp ? ' has-opp' : ''}`}
                onClick={(e) => { e.stopPropagation(); setAltTotalsLimit(l => l + 3); }}
              >
                {hiddenHasOpp ? '⚡ ' : ''}+{nextChunk} more
              </button>
            )}
            {remaining > nextChunk && (
              <button
                className="alt-expand-btn"
                onClick={(e) => { e.stopPropagation(); setAltTotalsLimit(Infinity); }}
              >
                See all {totalCount}
              </button>
            )}
            {isExp && (
              <button
                className="alt-expand-btn"
                onClick={(e) => { e.stopPropagation(); setAltTotalsLimit(3); }}
              >
                Back to main
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Sportsbook-style odds cell ───────────────────────────────────────────────
  const renderSBOddsCell = (key, odds, pointLabel, sd, isEV, isArb) => {
    if (odds == null) {
      return (
        <div className="sb-mkt-cell">
          <div className="sb-odds-btn disabled"><span className="sb-odds-val">—</span></div>
        </div>
      );
    }
    const cartEntry = selections.find(s =>
      s.eventId === sd.eventId && s.team === sd.team && (s.market || 'h2h') === (sd.market || 'h2h')
    );
    const inCart = Boolean(cartEntry);
    const tracked = trackedKeys.has(key);
    const isActive = activeCell?.key === key;
    const fmtO = o => o > 0 ? `+${o}` : `${o}`;
    const activeOdds = editedOddsMap[key] != null ? editedOddsMap[key] : odds;

    const handleClick = e => {
      e.stopPropagation();
      if (inCart) { removeSelection(cartEntry.id); if (isActive) setActiveCell(null); return; }
      setActiveCell(prev => prev?.key === key ? null : { key, stage: 'choice' });
      setTrackAmount('');
    };

    return (
      <div className="sb-mkt-cell">
        <button
          className={`sb-odds-btn${inCart ? ' in-cart' : ''}${isEV ? ' ev-plus' : ''}${isArb ? ' arb-opp' : ''}`}
          onClick={handleClick}
        >
          {(isEV || isArb) && (
            <span className="sb-badges">
              {isEV  && <span className="sb-badge sb-badge-ev">+EV</span>}
              {isArb && <span className="sb-badge sb-badge-arb">ARB</span>}
            </span>
          )}
          {pointLabel && <span className="sb-odds-point">{pointLabel}</span>}
          <span className="sb-odds-val">{inCart ? '✓' : fmtO(activeOdds)}</span>
          {tracked && !inCart && <span className="sb-tracked-dot">●</span>}
          {sd.sportsbookKey && (
            <div className="sb-btn-book">
              <SportsbookLogo bookKey={sd.sportsbookKey} size={12} />
              <span className="sb-btn-book-name">{SPORTSBOOK_NAMES[sd.sportsbookKey] || sd.sportsbook || ''}</span>
            </div>
          )}
        </button>
        {isActive && activeCell.stage === 'choice' && (
          <div className="compact-cell-actions" onClick={e => e.stopPropagation()}>
            <button className="cca-parlay" onClick={() => { addSelection(sd); setActiveCell(null); }}>+ Parlay</button>
            {session && !tracked && (
              <button className="cca-track" onClick={() => setActiveCell({ key, stage: 'track' })}>Track</button>
            )}
            <button className="cca-edit-odds" onClick={() => setActiveCell({ key, stage: 'edit' })}>
              {editedOddsMap[key] != null ? `Odds: ${fmtO(editedOddsMap[key])} ✎` : 'Edit odds'}
            </button>
            {session && tracked && <span className="cca-tracked-label">Tracked ✓</span>}
          </div>
        )}
        {isActive && activeCell.stage === 'edit' && (
          <div className="compact-track-form" onClick={e => e.stopPropagation()}>
            <input
              className="compact-track-input"
              value={editedOddsMap[key] != null ? String(editedOddsMap[key]) : ''}
              onChange={e => setEditedOddsMap(prev => ({ ...prev, [key]: e.target.value }))}
              placeholder={String(odds)}
              autoFocus
            />
            <button className="ctf-save" onClick={() => setActiveCell({ key, stage: 'choice' })}>✓</button>
            <button className="ctf-cancel" onClick={e => {
              e.stopPropagation();
              setEditedOddsMap(prev => { const n = { ...prev }; delete n[key]; return n; });
              setActiveCell({ key, stage: 'choice' });
            }}>✕</button>
          </div>
        )}
        {isActive && activeCell.stage === 'track' && (() => {
          const amt = parseFloat(trackAmount);
          const toWin = amt > 0
            ? (amt * (activeOdds > 0 ? activeOdds / 100 : 100 / Math.abs(activeOdds))).toFixed(2)
            : null;
          return (
            <div className="compact-track-form compact-track-form--col" onClick={e => e.stopPropagation()}>
              <div className="ctf-input-row">
                {editedOddsMap[key] != null && (
                  <span className="ctf-odds-label">{fmtO(editedOddsMap[key])}</span>
                )}
                <input type="number" className="compact-track-input" value={trackAmount}
                  onChange={e => setTrackAmount(e.target.value)} placeholder="$ amt" autoFocus min="0" step="1" />
                <button className="ctf-save" onClick={() => handleTrackBet(key, sd.team, activeOdds, sd.sportsbookKey, sd.market || 'h2h', sd.point ?? null)}>✓</button>
                <button className="ctf-cancel" onClick={e => { e.stopPropagation(); setActiveCell(null); }}>✕</button>
              </div>
              {toWin && <div className="ctf-to-win">To win: +${toWin}</div>}
            </div>
          );
        })()}
      </div>
    );
  };

  // ── Sportsbook-style horizontal table ────────────────────────────────────────
  const renderSportsbookTable = () => {
    if (!event.bookmakers || event.bookmakers.length === 0) return null;

    const bestHomeBook = event.bookmakers.reduce((b, c) => c.home_odds > b.home_odds ? c : b, event.bookmakers[0]);
    const bestAwayBook = event.bookmakers.reduce((b, c) => c.away_odds > b.away_odds ? c : b, event.bookmakers[0]);
    const bestDrawBook = hasDrawOdds
      ? event.bookmakers.reduce((best, b) => {
          if (!b.draw_odds) return best;
          return !best || b.draw_odds > best.draw_odds ? b : best;
        }, null)
      : null;

    const cs = event.consensus_spread;
    let spreadBooks = getBooksForPoint(event.spread_lines, cs);
    let effectiveSpreadPoint = cs;
    if (!spreadBooks && altLines?.spread_lines) {
      const altPts = Object.keys(altLines.spread_lines);
      if (altPts.length > 0) {
        effectiveSpreadPoint = Number(altPts.reduce((a, b) =>
          (altLines.spread_lines[b]?.length ?? 0) > (altLines.spread_lines[a]?.length ?? 0) ? b : a
        ));
        spreadBooks = altLines.spread_lines[effectiveSpreadPoint];
      }
    }
    const bestHomeSpread = spreadBooks?.reduce((b, c) => c.home_spread_odds > b.home_spread_odds ? c : b, spreadBooks[0]);
    const bestAwaySpread = spreadBooks?.reduce((b, c) => c.away_spread_odds > b.away_spread_odds ? c : b, spreadBooks[0]);
    const homeSpreadByBook = {};
    const awaySpreadByBook = {};
    (spreadBooks || []).forEach(b => { homeSpreadByBook[b.name] = b.home_spread_odds; awaySpreadByBook[b.name] = b.away_spread_odds; });

    const ct = event.consensus_total;
    const totalBooks = getBooksForPoint(event.total_lines, ct);
    const bestOverBook  = totalBooks?.reduce((b, c) => c.over_odds  > b.over_odds  ? c : b, totalBooks[0]);
    const bestUnderBook = totalBooks?.reduce((b, c) => c.under_odds > b.under_odds ? c : b, totalBooks[0]);
    const overByBook = {};
    const underByBook = {};
    (totalBooks || []).forEach(b => { overByBook[b.name] = b.over_odds; underByBook[b.name] = b.under_odds; });

    const isTennis = event.sport?.includes('tennis');
    const [homeGames, awayGames] = isTennis && event.clock ? event.clock.split('-').map(Number) : [null, null];
    const hasSpread = !isTennis && !!(bestHomeSpread?.home_spread_odds && bestAwaySpread?.away_spread_odds && effectiveSpreadPoint != null);
    const hasTotal  = !isTennis && !!(bestOverBook?.over_odds && bestUnderBook?.under_odds && ct != null);
    const mktCols   = (hasSpread ? 1 : 0) + 1 + (hasTotal ? 1 : 0);
    const gridCols  = `1fr ${Array(mktCols).fill('68px').join(' ')}`;

    const fmtShort = (ts) => new Date(ts).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });

    const periodStr = event.is_live ? (() => {
      if (event.is_halftime) return 'Halftime';
      if (event.inning_detail) return event.inning_detail;
      if (event.period) return formatPeriod(event.period, event.sport) + (!isTennis && event.clock ? ` · ${event.clock}` : '');
      return null;
    })() : null;

    // Compact situation bar JSX for live baseball
    const situationBar = event.is_live && event.sport?.includes('baseball') &&
      (event.outs != null || event.balls != null || event.on_first || event.on_second || event.on_third)
      ? (
        <div className="sb-situation-bar">
          {/* Base diamond */}
          <div className="sb-base-diamond">
            <div className={`sb-base sb-base-2b${event.on_second ? ' on' : ''}`} />
            <div className="sb-base-mid-row">
              <div className={`sb-base sb-base-3b${event.on_third ? ' on' : ''}`} />
              <div className="sb-base-gap" />
              <div className={`sb-base sb-base-1b${event.on_first ? ' on' : ''}`} />
            </div>
          </div>
          {/* Outs */}
          {event.outs != null && (
            <div className="sb-sit-group">
              <span className="sb-sit-label">Outs</span>
              <div className="sb-out-dots">
                {[0,1,2].map(i => (
                  <div key={i} className={`sb-out-dot${i < event.outs ? ' on' : ''}`} />
                ))}
              </div>
            </div>
          )}
          {/* Count */}
          {event.balls != null && event.strikes != null && (
            <div className="sb-sit-group">
              <span className="sb-sit-label">Count</span>
              <span className="sb-sit-val">{event.balls}-{event.strikes}</span>
            </div>
          )}
        </div>
      ) : null;

    return (
      <div className="sb-table">
        <div className="sb-header-row" style={{ gridTemplateColumns: gridCols }}>
          <div className="sb-league-label">
            {event.is_live && <span className="sb-live-pip">●</span>}
            {event.sport_title || ''}
            {periodStr && <span className="sb-period-inline"> · {periodStr}</span>}
            {isTennis && event.is_live && event.home_score != null && (
              <span className="sb-tennis-col-headers">
                <span>SETS</span><span>GAMES</span>
              </span>
            )}
          </div>
          {hasSpread && <div className="sb-market-header">SPREAD</div>}
          <div className="sb-market-header">MONEY</div>
          {hasTotal  && <div className="sb-market-header">TOTAL</div>}
        </div>

        {situationBar}

        <div className="sb-team-row" style={{ gridTemplateColumns: gridCols }}>
          <div className="sb-team-info">
            <TeamLogo teamName={event.home_team} sportKey={event.sport} size={20} />
            <span className="sb-team-name">{event.home_team}</span>
            {event.is_live && event.home_score != null && (
              isTennis ? (
                <span className="sb-tennis-score-cells">
                  <span className="sb-tennis-sets">{event.home_score}</span>
                  <span className="sb-tennis-games">{homeGames ?? '-'}</span>
                </span>
              ) : (
                <span className="sb-inline-score">{event.home_score}</span>
              )
            )}
          </div>
          {hasSpread && renderSBOddsCell(
            `spreads:${event.home_team}`,
            bestHomeSpread.home_spread_odds,
            formatPoint(effectiveSpreadPoint),
            { eventId: event.id, eventName, team: event.home_team, odds: bestHomeSpread.home_spread_odds,
              sportsbook: bestHomeSpread.title, sportsbookKey: bestHomeSpread.name,
              oddsAllBooks: homeSpreadByBook, market: 'spreads', point: effectiveSpreadPoint,
              sportKey: event.sport, homeTeam: event.home_team, awayTeam: event.away_team, commenceTime: event.commence_time },
            evSet.has(`${bestHomeSpread.name}:${event.home_team}:spreads:${effectiveSpreadPoint}`),
            arbHome[`spreads:${effectiveSpreadPoint}`] === bestHomeSpread.name
          )}
          {renderSBOddsCell(
            `h2h:${event.home_team}`,
            bestHomeBook.home_odds,
            null,
            { eventId: event.id, eventName, team: event.home_team, odds: bestHomeBook.home_odds,
              sportsbook: bestHomeBook.title, sportsbookKey: bestHomeBook.name,
              oddsAllBooks: homeOddsByBook, market: 'h2h',
              sportKey: event.sport, homeTeam: event.home_team, awayTeam: event.away_team, commenceTime: event.commence_time },
            evSet.has(`${bestHomeBook.name}:${event.home_team}:h2h:`),
            !hasDrawOdds && arbHome['h2h:'] === bestHomeBook.name
          )}
          {hasTotal && renderSBOddsCell(
            'totals:Over',
            bestOverBook.over_odds,
            `O ${ct}`,
            { eventId: event.id, eventName, team: 'Over', odds: bestOverBook.over_odds,
              sportsbook: bestOverBook.title, sportsbookKey: bestOverBook.name,
              oddsAllBooks: overByBook, market: 'totals', point: ct,
              sportKey: event.sport, homeTeam: event.home_team, awayTeam: event.away_team, commenceTime: event.commence_time },
            evSet.has(`${bestOverBook.name}:Over:totals:${ct}`),
            arbHome[`totals:${ct}`] === bestOverBook.name
          )}
        </div>

        {hasDrawOdds && bestDrawBook && (
          <div className="sb-team-row sb-draw-row" style={{ gridTemplateColumns: gridCols }}>
            <div className="sb-team-info">
              <span className="sb-team-name sb-draw-label">Draw</span>
            </div>
            {hasSpread && renderSBOddsCell('draw:spread', null, null, {}, false, false)}
            {renderSBOddsCell(
              'h2h:Draw',
              bestDrawBook.draw_odds,
              null,
              { eventId: event.id, eventName, team: 'Draw', odds: bestDrawBook.draw_odds,
                sportsbook: bestDrawBook.title, sportsbookKey: bestDrawBook.name,
                oddsAllBooks: drawOddsByBook, market: 'h2h',
                sportKey: event.sport, homeTeam: event.home_team, awayTeam: event.away_team, commenceTime: event.commence_time },
              evSet.has(`${bestDrawBook.name}:Draw:h2h:`),
              false
            )}
            {hasTotal && renderSBOddsCell('draw:total', null, null, {}, false, false)}
          </div>
        )}

        <div className="sb-team-row sb-away-row" style={{ gridTemplateColumns: gridCols }}>
          <div className="sb-team-info">
            <TeamLogo teamName={event.away_team} sportKey={event.sport} size={20} />
            <span className="sb-team-name">{event.away_team}</span>
            {event.is_live && event.away_score != null && (
              isTennis ? (
                <span className="sb-tennis-score-cells">
                  <span className="sb-tennis-sets">{event.away_score}</span>
                  <span className="sb-tennis-games">{awayGames ?? '-'}</span>
                </span>
              ) : (
                <span className="sb-inline-score">{event.away_score}</span>
              )
            )}
          </div>
          {hasSpread && renderSBOddsCell(
            `spreads:${event.away_team}`,
            bestAwaySpread.away_spread_odds,
            formatPoint(-effectiveSpreadPoint),
            { eventId: event.id, eventName, team: event.away_team, odds: bestAwaySpread.away_spread_odds,
              sportsbook: bestAwaySpread.title, sportsbookKey: bestAwaySpread.name,
              oddsAllBooks: awaySpreadByBook, market: 'spreads', point: -effectiveSpreadPoint,
              sportKey: event.sport, homeTeam: event.home_team, awayTeam: event.away_team, commenceTime: event.commence_time },
            evSet.has(`${bestAwaySpread.name}:${event.away_team}:spreads:${-effectiveSpreadPoint}`),
            arbAway[`spreads:${effectiveSpreadPoint}`] === bestAwaySpread.name
          )}
          {renderSBOddsCell(
            `h2h:${event.away_team}`,
            bestAwayBook.away_odds,
            null,
            { eventId: event.id, eventName, team: event.away_team, odds: bestAwayBook.away_odds,
              sportsbook: bestAwayBook.title, sportsbookKey: bestAwayBook.name,
              oddsAllBooks: awayOddsByBook, market: 'h2h',
              sportKey: event.sport, homeTeam: event.home_team, awayTeam: event.away_team, commenceTime: event.commence_time },
            evSet.has(`${bestAwayBook.name}:${event.away_team}:h2h:`),
            !hasDrawOdds && arbAway['h2h:'] === bestAwayBook.name
          )}
          {hasTotal && renderSBOddsCell(
            'totals:Under',
            bestUnderBook.under_odds,
            `U ${ct}`,
            { eventId: event.id, eventName, team: 'Under', odds: bestUnderBook.under_odds,
              sportsbook: bestUnderBook.title, sportsbookKey: bestUnderBook.name,
              oddsAllBooks: underByBook, market: 'totals', point: ct,
              sportKey: event.sport, homeTeam: event.home_team, awayTeam: event.away_team, commenceTime: event.commence_time },
            evSet.has(`${bestUnderBook.name}:Under:totals:${ct}`),
            arbAway[`totals:${ct}`] === bestUnderBook.name
          )}
        </div>

        <div className="sb-footer">
          <div className="sb-footer-left">
            {event.is_live && <span className="live-badge-mini"><span className="live-dot"></span>LIVE</span>}
            <span className="sb-game-time-label">{fmtShort(event.commence_time)}</span>
            {event.sport_title && <span className="sb-sport-badge">{event.sport_title}</span>}
          </div>
          <span className="sb-more-wagers">More wagers →</span>
        </div>
      </div>
    );
  };

  const handleCardClick = (e) => {
    if (e.target.closest('.odds-button') || e.target.closest('.quick-bet-link') ||
        e.target.closest('.compact-cell-actions') || e.target.closest('.compact-track-form') ||
        e.target.closest('.clear-highlight-btn')) {
      return;
    }
    setActiveCell(null);
    if (onSelectGame) {
      onSelectGame(event.id, 'h2h', event.sport);
    }
  };


  return (
    <div
      ref={highlightRef}
      className={`game-card ${event.is_live ? 'live' : ''} ${isHighlighted ? 'highlighted' : ''}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      {isHighlighted && onClearHighlight && (
        <button className="clear-highlight-btn" onClick={onClearHighlight} title="Clear highlight">
          <X size={16} />
        </button>
      )}

      {event.is_live && (event.home_score != null || event.period != null || event.inning_detail || event.is_halftime) && (
        <div className="live-scoreboard">
          {event.home_score != null && (
            <>
              <div className="scoreboard-row">
                <span className="scoreboard-team">{shortTeamName(event.home_team)}</span>
                <span className="scoreboard-score">{event.home_score}</span>
              </div>
              <div className="scoreboard-row">
                <span className="scoreboard-team">{shortTeamName(event.away_team)}</span>
                <span className="scoreboard-score">{event.away_score ?? '-'}</span>
              </div>
            </>
          )}
          {event.is_halftime && (
            <div className="scoreboard-period">Halftime</div>
          )}
          {!event.is_halftime && event.period != null && !event.sport?.includes('baseball') && (
            <div className="scoreboard-period">
              {formatPeriod(event.period, event.sport)}{event.clock ? ` · ${event.clock}` : ''}
            </div>
          )}
          {!event.is_halftime && event.inning_detail && event.sport?.includes('baseball') && (
            <div className="baseball-situation">
              <span className="inning-label">{event.inning_detail}</span>
              <div className="situation-row">
                <div className="base-diamond">
                  <div className={`base base-2b${event.on_second ? ' on' : ''}`} />
                  <div className="base-mid-row">
                    <div className={`base base-3b${event.on_third ? ' on' : ''}`} />
                    <div className="base-gap" />
                    <div className={`base base-1b${event.on_first ? ' on' : ''}`} />
                  </div>
                </div>
                <div className="outs-wrap">
                  <span className="sit-label">OUT</span>
                  <div className="outs-dots">
                    {[0, 1, 2].map(i => (
                      <div key={i} className={`out-dot${i < (event.outs ?? 0) ? ' on' : ''}`} />
                    ))}
                  </div>
                </div>
                {event.balls != null && event.strikes != null && (
                  <div className="count-wrap">
                    <span className="sit-label">CNT</span>
                    <span className="count-val">{event.balls}-{event.strikes}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {event.bookmakers && event.bookmakers.length > 0 ? (
        <div className="compact-markets">
          {renderSportsbookTable()}
        </div>
      ) : (
        <div className="no-lines-live">Lines not available while game is in progress</div>
      )}

      {isExpanded && (
        <>
          <div className="market-section">
            <div className="market-section-title">Moneyline</div>
            {renderH2hOdds()}
          </div>

          {event.has_spreads && (
            <div className="market-section">
              <div className="market-section-title">Spread</div>
              {renderConsensusSpreadOdds()}
            </div>
          )}

          {altLines && Object.keys(altLines.spread_lines || {}).length > 0 && (
            <div className="market-section">
              <div className="market-section-title">{event.has_spreads ? 'Alt Spreads' : 'Spread'}</div>
              {renderAltSpreadOdds()}
            </div>
          )}

          {event.has_totals && (
            <div className="market-section">
              <div className="market-section-title">Totals</div>
              {renderConsensusTotalsOdds()}
            </div>
          )}

          {altLines && Object.keys(altLines.total_lines || {}).length > 0 && (
            <div className="market-section">
              <div className="market-section-title">{event.has_totals ? 'Alt Totals' : 'Totals'}</div>
              {renderAltTotalsOdds()}
            </div>
          )}

          {altLoading && <p className="no-market-data">Loading alt lines…</p>}
        </>
      )}
    </div>
  );
}

export default GameCard;
